import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応（既存remotionコンポジション踏襲）
import { Player } from '@remotion/player';
import ugcAdService, { UgcAdProject, UgcAdScene, UgcAdCaptionSync } from '../../services/api/ugcAdService';
import { TaskyButton, TaskyEmptyState, TaskyAlert, TaskyCard, TaskySwitch, TaskyInput } from '../tasky';
import {
  UgcAdComposition,
  deriveUgcCompositionScenes,
  deriveUgcCtaCard,
  getUgcAdDimensions,
  getUgcAdDurationInFrames,
  UGC_AD_FPS,
  stabilizeUgcCompositionMedia,
  orderedIncludedScenes,
} from '../../remotion/ugc-ad';
import { resolveTotalSeconds } from './sceneTimelineEditor';
import { cn } from '../../lib/utils';
import { Film, Download, SlidersHorizontal, PenSquare } from 'lucide-react';

/**
 * ステップ⑤: 完成プレビュー（song-ad PreviewStep 踏襲）。
 *
 * @remotion/player でインライン再生する。「このまま書き出す」「詳細編集する」の2導線のみ。
 * CTA（締めカット）のON/OFFと文言をここで制御する。
 * トーク中心シーンのクリップは発話音声込みで再生される（UgcAdComposition はクリップをミュートしない）。
 *
 * presigned URL の再発行によるメディア再ロード（再生位置が先頭へ戻る問題）は
 * `stabilizeUgcCompositionMedia()` でURL文字列値を安定化して防ぐ（song-ad v5 k と同じ対策）。
 */

const CTA_TEXT_DEBOUNCE_MS = 1000;

interface PreviewStepProps {
  project: UgcAdProject;
  scenes: UgcAdScene[];
  onProceedToExport: () => void;
  onProceedToEditor: () => void;
  /** CTAカードのON/OFFをPATCHする（cta_enabled） */
  onUpdateCtaEnabled: (enabled: boolean) => Promise<void>;
  /** CTA文言をPATCHする（advanced_settings.cta。空文字は商品名フォールバックのためnullで送る） */
  onUpdateCtaText: (text: string) => Promise<void>;
  /** ①情報登録タブへの変更導線。ロゴの選択・変更はそちらで行う */
  onEditSettings?: () => void;
  /** テロップと音声の合わせ方を切り替える（advanced_settings.caption_sync） */
  onUpdateCaptionSync?: (mode: UgcAdCaptionSync) => Promise<void>;
}

const PreviewStep: React.FC<PreviewStepProps> = ({
  project,
  scenes,
  onProceedToExport,
  onProceedToEditor,
  onUpdateCtaEnabled,
  onUpdateCtaText,
  onEditSettings,
  onUpdateCaptionSync,
}) => {
  const missingStoryboards = scenes.filter((s) => !s.storyboard.image);
  const canPreview = scenes.length > 0 && missingStoryboards.length === 0;
  /** 「動画化」指定なのにクリップ未生成のシーン。このまま書き出すと静止画に落ちる（レビューP1の警告対象） */
  const pendingClips = scenes.filter((s) => s.motion_mode === 'clip' && !s.clip.video);

  // 詳細編集でシーンの尺を変えていれば動画全体の長さもそれに追従する（総尺可変）。
  // ここが書き出し側（UgcAdRenderPlanner）とズレるとプレビューと完成動画が食い違う
  const totalSeconds = useMemo(
    () =>
      resolveTotalSeconds(
        orderedIncludedScenes(scenes, project.timeline).map((entry) => entry.scene),
        project.timeline?.scene_durations,
        project.target_seconds
      ),
    [scenes, project.timeline, project.target_seconds]
  );
  const compositionScenes = useMemo(
    () => deriveUgcCompositionScenes(scenes, project.timeline, totalSeconds),
    [scenes, project.timeline, totalSeconds]
  );
  const { scenes: renderScenes, cta } = useMemo(
    () => deriveUgcCtaCard(compositionScenes, project, totalSeconds),
    [compositionScenes, project, totalSeconds]
  );
  const captions = project.timeline?.captions ?? [];
  const dimensions = getUgcAdDimensions();
  const durationInFrames = getUgcAdDurationInFrames(totalSeconds);
  const stableMedia = useMemo(
    () => stabilizeUgcCompositionMedia(renderScenes, cta, null),
    [renderScenes, cta]
  );

  const ctaEnabled = project.cta?.enabled ?? project.cta_enabled;
  const ctaLogoUrl = project.cta?.logo_url ?? null;
  const savedCtaText = project.advanced_settings?.cta ?? '';

  const [ctaTextDraft, setCtaTextDraft] = useState(savedCtaText);
  const [isTogglingCta, setIsTogglingCta] = useState(false);
  const ctaTextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // サーバー側の値（他タブでの変更・ポーリング等）が変わったらドラフトを同期する
  useEffect(() => {
    setCtaTextDraft(savedCtaText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCtaText]);

  useEffect(
    () => () => {
      if (ctaTextTimerRef.current) clearTimeout(ctaTextTimerRef.current);
    },
    []
  );

  // CTA候補（UGC-07）。広告の中身を全部AIが作ったあとで最後の一言だけ
  // 利用者に考えさせないよう、初回表示時に候補を取りに行く
  const [ctaSuggestions, setCtaSuggestions] = useState<string[]>([]);
  const ctaSuggestionsRequestedRef = useRef(false);

  useEffect(() => {
    if (ctaSuggestionsRequestedRef.current || !ctaEnabled) return;
    ctaSuggestionsRequestedRef.current = true;

    ugcAdService
      .getCtaSuggestions(project.id)
      // 候補が取れなくてもCTAは手入力できるので、失敗は黙って無視する
      .then(setCtaSuggestions)
      .catch(() => setCtaSuggestions([]));
  }, [project.id, ctaEnabled]);

  const applyCtaSuggestion = (text: string) => {
    setCtaTextDraft(text);
    commitCtaText(text);
  };

  const captionSync = project.advanced_settings?.caption_sync ?? 'proportional';
  const [isUpdatingCaptionSync, setIsUpdatingCaptionSync] = useState(false);

  const handleCaptionSyncChange = async (useWhisper: boolean) => {
    if (!onUpdateCaptionSync) return;
    setIsUpdatingCaptionSync(true);
    try {
      await onUpdateCaptionSync(useWhisper ? 'whisper' : 'proportional');
    } finally {
      setIsUpdatingCaptionSync(false);
    }
  };

  const handleToggleCta = async (checked: boolean) => {
    setIsTogglingCta(true);
    try {
      await onUpdateCtaEnabled(checked);
    } finally {
      setIsTogglingCta(false);
    }
  };

  const commitCtaText = (text: string) => {
    if (ctaTextTimerRef.current) {
      clearTimeout(ctaTextTimerRef.current);
      ctaTextTimerRef.current = null;
    }
    if (text === savedCtaText) return;
    onUpdateCtaText(text);
  };

  // 入力途中で毎回PATCHしない。1秒debounceし、フォーカスが外れたらすぐ確定させる
  const handleCtaTextChange = (text: string) => {
    setCtaTextDraft(text);
    if (ctaTextTimerRef.current) clearTimeout(ctaTextTimerRef.current);
    ctaTextTimerRef.current = setTimeout(() => commitCtaText(text), CTA_TEXT_DEBOUNCE_MS);
  };

  if (!canPreview) {
    return (
      <TaskyEmptyState
        icon={<Film />}
        title="先にストーリーボードを完成させてください"
        description="「③ ストーリーボード」で全シーンの画像を揃えるとプレビューできます"
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-5">
        <h2 className="text-lg font-semibold text-dark">完成動画のプレビュー</h2>
      </div>

      {pendingClips.length > 0 && (
        <TaskyAlert tone="warning" className="mb-4">
          「動画化」に設定したシーンのうち{pendingClips.length}件がまだクリップ未生成です。このまま書き出すと該当シーンは静止画になります。「⑤ モーション」で生成してから書き出すのがおすすめです。
        </TaskyAlert>
      )}

      {/* プレビュー映像＋アクション（song-ad v8 と同じ横並びレイアウト。
          flex子で width を明示しないと幅0に収束する罠も同じなので width を数値指定する） */}
      <div className="bg-[#F4F3EF] rounded-2xl p-5 mb-5 flex flex-col items-center gap-5 sm:flex-row sm:items-center">
        <div
          className="bg-black rounded-xl overflow-hidden mx-auto shadow shrink-0"
          style={{
            aspectRatio: `${dimensions.width} / ${dimensions.height}`,
            width: 360,
            maxWidth: '100%',
          }}
        >
          <Player
            key={`${durationInFrames}-${compositionScenes.length}`}
            component={UgcAdComposition}
            inputProps={{
              scenes: stableMedia.scenes,
              captions,
              bgmUrl: stableMedia.bgmUrl,
              cta: stableMedia.cta,
            }}
            durationInFrames={durationInFrames}
            fps={UGC_AD_FPS}
            compositionWidth={dimensions.width}
            compositionHeight={dimensions.height}
            style={{ width: '100%', height: '100%' }}
            controls
            loop
          />
        </div>

        <div className="flex flex-col items-center gap-3 sm:flex-1 sm:items-start">
          <TaskyButton onClick={onProceedToExport} className="w-full sm:w-auto">
            <Download className="w-4 h-4" />
            このまま書き出す
          </TaskyButton>
          <TaskyButton variant="outline" onClick={onProceedToEditor} className="w-full sm:w-auto">
            <SlidersHorizontal className="w-4 h-4" />
            詳細編集する
          </TaskyButton>
          <p className="text-center text-[11px] text-gray-400 sm:text-left">
            テロップの文言・タイミングは「詳細編集」で直せます。
          </p>
        </div>
      </div>

      {onUpdateCaptionSync && (
        <TaskyCard variant="warm" className="mb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-dark">テロップと音声の合わせ方</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                既定はクリップの長さに合わせて配分します。話し始めのタイミングまで合わせたい場合は
                「音声を解析して合わせる」を選んでください（書き出しに時間がかかります）。
              </p>
            </div>
            <TaskySwitch
              checked={captionSync === 'whisper'}
              disabled={isUpdatingCaptionSync}
              onChange={(e) => handleCaptionSyncChange(e.target.checked)}
              label="音声を解析して合わせる"
            />
          </div>
        </TaskyCard>
      )}

      <TaskyCard variant="warm" className="mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-dark">CTA（締めカット）</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              動画の最後に約2.5秒のカットとして入ります（動画全体の長さは変わりません）
            </p>
          </div>
          <TaskySwitch
            checked={ctaEnabled}
            disabled={isTogglingCta}
            onChange={(e) => handleToggleCta(e.target.checked)}
            label="動画の最後にCTAを入れる"
          />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-start">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5" htmlFor="ugc-ad-cta-text">
              CTA文言
            </label>
            <TaskyInput
              id="ugc-ad-cta-text"
              value={ctaTextDraft}
              disabled={!ctaEnabled}
              maxLength={100}
              placeholder="例: 今すぐチェック"
              onChange={(e) => handleCtaTextChange(e.target.value)}
              onBlur={() => commitCtaText(ctaTextDraft)}
            />
            <p className="text-[11px] text-gray-400 mt-1">未入力のときは商品名が使われます</p>

            {/* AIが出した候補。押すとそのまま反映され、あとから自由に直せる */}
            {ctaEnabled && ctaSuggestions.length > 0 && (
              <div className="mt-2">
                <span className="text-[11px] text-gray-500">AIの提案:</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {ctaSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => applyCtaSuggestion(suggestion)}
                      className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                        ctaTextDraft === suggestion
                          ? 'border-primary bg-primary text-white'
                          : 'border-warm-border text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={cn('flex flex-col gap-1', !ctaEnabled && 'opacity-50')}>
            <span className="text-xs font-medium text-gray-600">ロゴ</span>
            {ctaLogoUrl ? (
              <div className="flex items-center gap-2">
                <img
                  src={ctaLogoUrl}
                  alt="CTAロゴ"
                  className="w-10 h-10 rounded-md object-contain bg-white border border-warm-border shrink-0"
                />
                <span className="text-[11px] text-gray-400 max-w-[9rem]">
                  ①情報登録で指定したロゴ、未指定時は商品のロゴ画像→選択画像の1枚目が使われます
                </span>
              </div>
            ) : (
              <span className="text-[11px] text-gray-400 max-w-[9rem]">
                ロゴ未登録（①情報登録でロゴを指定するか、商品にロゴ画像を登録すると自動で入ります）
              </span>
            )}
            {onEditSettings && (
              <button
                type="button"
                onClick={onEditSettings}
                className="flex items-center gap-1 text-[11px] text-primary hover:underline mt-0.5 self-start"
              >
                <PenSquare className="w-3 h-3" />
                ロゴを変更
              </button>
            )}
          </div>
        </div>
      </TaskyCard>
    </div>
  );
};

export default PreviewStep;
